// materialGraphTheme.js — Theme system for the shader graph editor.
// Exports CSS variable palettes, pin/data-type colors, category colors, and helpers.

export const SG_PALETTES = {
  // Colors live in materialGraph.css, which rebinds the sg vars (--bg, --panel,
  // --accent, …) to the global --m-* palette tokens (ENG-880) — so the editor
  // follows the user's selected theme. Inlining hex values here would override
  // the stylesheet (inline style beats :root) and freeze the theme; a palette
  // entry now only carries non-color knobs.
  indigo: { label: 'Indigo', vars: {} },
}

export const SG_DENSITY = {
  compact: { '--hd-h': '22px', '--row-h': '19px', '--row-fs': '10.5px', '--node-pad': '8px' },
}

// Data-type colors (theme-independent for wire readability)
export const TYPE_COLORS = {
  float: '#9ad36b',
  vec2: '#54cbe0',
  vec3: '#f3b54a',
  vec4: '#e08bd0',
  color: '#f3b54a',
  bool: '#ef6a82',
  shader: '#bda4ff',
}

// Category colors (drive header accents + dots)
export const CAT_COLORS = {
  input: '#54cbe0',
  param: '#f3b54a',
  texture: '#54cbe0',
  math: '#9ad36b',
  shader: '#bda4ff',
  output: '#8b6cff', // uses --accent
}

// Map node type → category for palette grouping + styling
const NODE_CATEGORIES = {
  PrincipledBSDF: 'output',
  Fresnel: 'math',
  Multiply: 'math',
  Add: 'math',
  Lerp: 'math',
  ConstantFloat: 'param',
  ConstantColor: 'param',
  Time: 'input',
  UV: 'input',
  Panner: 'texture',
  TextureSample: 'texture',
  ComponentMask: 'math',
  Split: 'math',
  Combine: 'math',
  Subtract: 'math',
  Divide: 'math',
  OneMinus: 'math',
  Saturate: 'math',
  Power: 'math',
  Dot: 'math',
  Normalize: 'math',
  Sine: 'math',
  Step: 'math',
  ColorRamp: 'math',
  Smoothstep: 'math',
  Clamp: 'math',
  Remap: 'math',
  Min: 'math',
  Max: 'math',
  Abs: 'math',
  Sign: 'math',
  Floor: 'math',
  Ceil: 'math',
  Fract: 'math',
  Mod: 'math',
  Cosine: 'math',
  Sqrt: 'math',
  Distance: 'math',
  Length: 'math',
  Cross: 'math',
  Reflect: 'math',
  Luminance: 'math',
  Gamma: 'math',
  MakeVector2: 'math',
  BreakVector2: 'math',
  MakeVector4: 'math',
  Rotate2D: 'math',
  Tangent: 'math',
  ArcTan2: 'math',
  Exp: 'math',
  Log: 'math',
  Noise: 'texture',
  NormalMap: 'texture',
  BrightContrast: 'math',
  HueSatVal: 'math',
  Desaturation: 'math',
  WorldPosition: 'input',
  WorldNormal: 'input',
  ViewDirection: 'input',
}

export function categoryOf(nodeType) {
  return NODE_CATEGORIES[nodeType] || 'param'
}

export function pinColor(type) {
  return TYPE_COLORS[type] || TYPE_COLORS.float
}

// Create CSS var object for spreading on root element
export function getCSSVars(paletteKey = 'indigo', densityKey = 'compact') {
  const palette = SG_PALETTES[paletteKey] || SG_PALETTES.indigo
  const density = SG_DENSITY[densityKey] || SG_DENSITY.compact
  return {
    ...palette.vars,
    ...density,
  }
}

// Brightness glow color for category header
export function categoryGlow(category) {
  return CAT_COLORS[category] || CAT_COLORS.param
}
